/**
 * Project slice reducer — the largest Redux slice, managing all project-level state.
 *
 * Handles three broad areas:
 * 1. Data ingestion config: collections (file groups), column mappings (canonicals),
 *    custom columns, join configuration, and collection inclusion/exclusion.
 * 2. Filtering: binary value filters (include/exclude specific values per column)
 *    and regex filters. The filtersHaveChanged flag tracks unsaved filter changes.
 * 3. Analysis flags: has* booleans (hasAging, hasRollforward, etc.) track which
 *    analyses have been computed. These are set by data actions after successful computation.
 *
 * State is auto-saved to MongoDB on every change via saveProject().
 */
const initialState = {
  filters: [],
  regexFilters: [],
  current: null, // currently selected collection ID
};

// -------------------------------------------------------
// -------------------------------------------------------
const reducer = (state = initialState, action) => {
  let filters;
  // -------------------------------------------------------
  switch (action.type) {

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_PROJECT_ANALYSIS":
      return {
        ...state,
        hasAnalysis: action.hasAnalysis,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_PROJECT_PARAMETERS":
      return {
        ...state,
        parameters: action.parameters,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_NORM_MAP":
      return {
        ...state,
        normMap: action.normMap,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_INVOICE_PARAMETERS":
      return {
        ...state,
        invoiceParameters: action.invoiceParameters,
        hasInvoiceCalculated: false,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_INVOICE_CALCULATED":
      return {
        ...state,
        hasInvoiceCalculated: true,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------

    case "CHANGE_FILTER_STATUS":
      return {
        ...state,
        filtersHaveChanged: action.filtersHaveChanged,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    // Moves a value from the excluded list to the included list for a specific column filter
    case "ADD_VALUE":
      return {
        ...state,
        filters: state.filters.map((filter) =>
          filter.column === action.column
            ? { ...filter, included: [...filter.included, action.value], excluded: filter.excluded.filter((v) => v !== action.value) }
            : filter
        ),
        filtersHaveChanged: true,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "REMOVE_VALUE":
      return {
        ...state,
        filters: state.filters.map((filter) =>
          filter.column === action.column
            ? { ...filter, included: filter.included.filter((v) => v !== action.value), excluded: [...filter.excluded, action.value] }
            : filter
        ),
        filtersHaveChanged: true,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "INCLUDE_ALL":
      return {
        ...state,
        filters: state.filters.map((filter) =>
          filter.column === action.column
            ? { ...filter, included: [...filter.all], excluded: [] }
            : filter
        ),
        filtersHaveChanged: true,
      };

    case "EXCLUDE_ALL":
      return {
        ...state,
        filters: state.filters.map((filter) =>
          filter.column === action.column
            ? { ...filter, included: [], excluded: [...filter.all] }
            : filter
        ),
        filtersHaveChanged: true,
      };

    case "INVERT_FILTER":
      return {
        ...state,
        filters: state.filters.map((filter) =>
          filter.column === action.column
            ? { ...filter, included: filter.excluded, excluded: filter.included }
            : filter
        ),
        filtersHaveChanged: true,
      };

    case "ADD_FILTER":
      return {
        ...state,
        filters: [...state.filters, action.filter],
        filtersHaveChanged: true,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "REMOVE_FILTER":
      return {
        ...state,
        filters: [...state.filters].filter(
          (filter) => filter.column !== action.column
        ),
        filtersHaveChanged: true,
      };

    // -------------------------------------------------------
    // REGEX FILTER CASES
    // -------------------------------------------------------
    case "CHANGE_REGEX_FILTER_STATUS":
      return {
        ...state,
        filtersHaveChanged: action.regexFiltersHaveChanged,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "ADD_REGEX_FILTER":
      return {
        ...state,
        regexFilters: [...state.regexFilters, action.filter],
        filtersHaveChanged: true,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "REMOVE_REGEX_FILTER":
      return {
        ...state,
        regexFilters: [...state.regexFilters].filter(
          (filter) => filter.column !== action.column
        ),
        filtersHaveChanged: true,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "UPDATE_REGEX_PATTERN":
      return {
        ...state,
        regexFilters: state.regexFilters.map((filter) => {
          if (filter.column === action.column) {
            return { ...filter, pattern: action.pattern };
          }
          return filter;
        }),
        filtersHaveChanged: true,
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    // Any recalculation invalidates the saved report
    case "SET_HAS_AGING":
      return {
        ...state,
        hasAging: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_ROLLFORWARD":
      return {
        ...state,
        hasRollforward: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_SUPPLIER":
      return {
        ...state,
        hasSupplier: true,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_MASTER":
      return {
        ...state,
        hasMaster: true,
      };
    case "SET_REFERENCE_DATE":
      return {
        ...state,
        parameters: {
          ...state.parameters,
          referenceDate: action.referenceDate,
        },
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_DELINQUENCIES":
      return {
        ...state,
        hasDelinquencies: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_DILUTION":
      return {
        ...state,
        hasDilution: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_LOSS":
      return {
        ...state,
        hasLoss: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_YIELD":
      return {
        ...state,
        hasYield: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_AGGREGATE_RESERVE":
      return {
        ...state,
        hasAggregateReserve: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_BUYER_PARENT":
      return {
        ...state,
        hasBuyerParent: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_CONCENTRATION_LIMITS":
      return {
        ...state,
        hasConcentrationLimits: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_PROJECT_REPORT":
      return {
        ...state,
        savedReport: action.savedReport,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_DOC_TYPE_SUMS":
      return {
        ...state,
        docTypeSums: action.docTypeSums,
        docTypeDescriptions: action.docTypeDescriptions,
      };
    case "SET_MASTER_VIEW_COLUMNS":
      return {
        ...state,
        masterViewColumns: action.columns,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_RATING_SCALE":
      return {
        ...state,
        ratingScale: action.ratingScale,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_CALC_PARAMS":
      return {
        ...state,
        calcParams: {
          ...state.calcParams,
          [action.section]: action.params,
        },
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_BUYER_RATINGS":
      return {
        ...state,
        buyerRatings: action.buyerRatings,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_HAS_NERB":
      return {
        ...state,
        hasNerb: true,
        savedReport: null,
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_ANALYSIS_STATUS":
      return {
        ...state,
        ...action.status,
      };
    // -------------------------------------------------------
    // Scenarios (what-if analyses). Persisted with the project doc, so the list
    // survives reloads. Scenario RESULTS and has* flags live in the data slice.
    // -------------------------------------------------------
    case "ADD_SCENARIO":
      return {
        ...state,
        scenarios: [...(state.scenarios || []).filter((s) => s.id !== action.scenario.id), action.scenario],
      };

    case "UPDATE_SCENARIO":
      return {
        ...state,
        scenarios: (state.scenarios || []).map((s) =>
          s.id === action.id ? { ...s, ...action.patch } : s
        ),
      };

    case "REMOVE_SCENARIO":
      return {
        ...state,
        scenarios: (state.scenarios || []).filter((s) => s.id !== action.id),
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "CLEAR_PROJECT":
      return initialState;

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "INCLUDE_COLLECTION":
      return {
        ...state,
        included: [...state.included, action.collectionid],
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "EXCLUDE_COLLECTION":
      return {
        ...state,
        included: state.included.filter(
          (collectionid) => collectionid !== action.collectionid
        ),
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    // Removes a column mapping (header -> canonical name) from a specific collection
    case "REMOVE_CANONICAL":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              delete collection.mapping[action.header];
            }
            return collection;
          }
        ),
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    // Sets or updates a column mapping: maps a raw CSV header to a canonical field name (e.g., "Inv Date" -> "issue_date")
    case "UPDATE_CANONICAL":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.mapping = {
                ...collection.mapping,
                [action.header]: action.canonical,
              };
            }
            return collection;
          }
        ),
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "ERASE_CUSTOM_COLUMN":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.custom = collection.custom.filter(
                (row, index) => index !== action.index
              );
            }
            return collection;
          }
        ),
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "RESET_JOIN":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.join = {};
            }
            return collection;
          }
        ),
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    // Configures a lookup/join file for a collection (e.g., joining a buyer master list to transaction data)
    case "SET_JOIN_FILE":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.join = {
                file: action.file,
                headers: action.headers,
              };
            }
            return collection;
          }
        ),
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_JOIN_FILE_MATCH":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.join = {
                ...collection.join,
                fileMatch: action.match,
              };
            }
            return collection;
          }
        ),
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "SET_JOIN_COLLECTION_MATCH":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.join = {
                ...collection.join,
                collectionMatch: action.match,
              };
            }
            return collection;
          }
        ),
      };
    // -------------------------------------------------------
    // -------------------------------------------------------
    case "REMOVE_JOIN_HEADER":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.join = {
                ...collection.join,
                headers: collection.join.headers.filter(
                  (header) => header !== action.header
                ),
              };
            }
            return collection;
          }
        ),
      };

    // -------------------------------------------------------
    // -------------------------------------------------------
    case "UPDATE_CUSTOM_COLUMN":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              collection.custom = collection.custom.map((row, index) => {
                if (index === action.index) {
                  row.key = action.key;
                  row.value = action.value;
                }
                return row;
              });
            }
            return collection;
          }
        ),
      };
    // --------------------------------------------------------------
    // --------------------------------------------------------------
    case "ADD_CUSTOM_COLUMN":
      return {
        ...state,
        collections: JSON.parse(JSON.stringify(state.collections)).map(
          (collection) => {
            if (collection.id === action.collectionid) {
              if (collection.custom === undefined) collection.custom = [];
              collection.custom.push({ key: "", value: "" });
            }
            return collection;
          }
        ),
      };
    // --------------------------------------------------------------
    // --------------------------------------------------------------
    case "SET_CURRENT_COLLECTION":
      return {
        ...state,
        current: action.current,
      };

    // --------------------------------------------------------------
    // --------------------------------------------------------------
    case "SET_BUYER_VIEW":
      return {
        ...state,
        buyerView: action.buyerView,
      };
    // --------------------------------------------------------------
    // --------------------------------------------------------------
    case "SET_PROJECT":
      return {
        ...state,
        ...action.project,
      };

    // --------------------------------------------------------------
    // Attaches an uploaded directory of files to the project. Updates both the directory list
    // and the flat file repository (id-keyed lookup for quick access).
    case "ATTACH_DIR_TO_PROJECT":
      const repository = JSON.parse(JSON.stringify(state.repository));
      action.flatFiles.forEach((file) => {
        repository[file.id] = file;
      });

      return {
        ...state,
        dirs: [...state.dirs, action.dir],
        flatFiles: [...state.flatFiles, ...action.flatFiles],
        repository,
      };
    // --------------------------------------------------------------
    case "SET_COLLECTIONS":
      return {
        ...state,
        collections: action.collections,
      };
    // --------------------------------------------------------------
    case "ERASE_COLLECTION":
      return {
        ...state,
        collections: state.collections.filter(
          (collection) => collection.id !== action.collectionid
        ),
        included: state.included.filter(
          (collectionid) => collectionid !== action.collectionid
        ),
      };

    // --------------------------------------------------------------
    default:
      return state;
  }
};

export default reducer;
